import axios, { AxiosInstance } from 'axios';

const isUnauthorizedTokenError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  if (err.response?.status !== 401) return false;
  return (
    (err.response.data as { code?: string } | undefined)?.code ===
    'unauthorized'
  );
};

/**
 * Response interceptor para clientes axios usados desde Server Components / Server Actions.
 *
 * Cuando el backend responde 401 con `code: 'unauthorized'` (sesión vencida o token inválido),
 * dispara `redirect('/login')` de Next.js. Esto lanza una excepción `NEXT_REDIRECT` que Next
 * intercepta antes del Error Boundary, evitando la pantalla 500 y la notificación a Slack.
 *
 * En el cliente (`typeof window !== 'undefined'`) el error se deja pasar tal cual: cada hook
 * que use axios desde Client Components maneja sus 401 con `useError` / react-query.
 */
export const attachAuthInterceptor = (
  instance: AxiosInstance
): AxiosInstance => {
  instance.interceptors.response.use(
    (response) => response,
    async (err) => {
      if (isUnauthorizedTokenError(err) && typeof window === 'undefined') {
        const { redirect } = await import('next/navigation');
        redirect('/login');
      }
      throw err;
    }
  );
  return instance;
};
